import { Field, Form, FormikProps } from 'formik';
import { FC, useMemo, useState } from 'react';
import { Box, Modal as QcsModal } from '@mui/material';
import { EntryRequestDto } from '@api/models/invitation/EntryRequestDto';
import { QcsModalBox } from '../common/basic/QcsModalBox';
import { SectionHeader } from '../common/SectionHeader';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FormContainer } from '../common/form/FormContainer';
import { InvitationPersonAutocomplete } from '../common/form/InvitationPersonAutocomplete';
import { QcsTimePicker } from '../common/form/QcsTimePicker';
import { EstablishmentAutocomplete } from '../common/form/EstablishmentAutocomplete';
import { useTranslation } from 'react-i18next';
import { InvitationWorkplaceAutocomplete } from '../common/form/InvitationWorkplaceAutocomplete';
import { EntryRequestState } from '@api/models/enums/EntryRequestState';
import { entryRequestService } from '@api/services/services';
import { QcsAutocomplete } from '../common/form/QcsAutocomplete';
import { DatePicker } from '../common/form/DatePicker';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { QcsTextField } from '../common/basic/QcsTextField';
import axios, { CancelToken } from 'axios';
import { entryRequestListActions, getEntryRequestList } from '../../store/entities/entryRequestList';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectIdentity } from '../../store/entities/identity';
import { hasRole } from '../../utils/roles';
import { getFirstLastNameObj } from '../../utils/format';
import { formatDate } from '../../utils/date';
import { entryRequestInvitationListActions } from '../../store/entities/entryRequestInvitationList';
import { InvitationDto } from '@api/models/invitation/InvitationDto';
import { UserRole } from '@api/models/enums/UserRole';

interface Props {
  data: EntryRequestDto;
  onClose: () => void;
  disabled?: boolean;
}

type FormValues = Partial<EntryRequestDto> & { reason?: string };

export const RequestEntryInfoModal: FC<Props> = ({ data, onClose, disabled }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  const establishments = useMemo(
    () => (data.establishment ? [data.establishment] : []),
    [data.establishment]
  );
  const [reasonError, setReasonError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const identity = useAppSelector(selectIdentity);

  const handleRefresh = (cancelToken?: CancelToken) => {
    dispatch(entryRequestListActions.reload(true));
    dispatch(entryRequestInvitationListActions.reload(true));
    dispatch(getEntryRequestList(cancelToken ?? axios.CancelToken.source().token));
  };

  const changeState = async (state: EntryRequestState, formik: FormikProps<FormValues>) => {
    if (state === EntryRequestState.REJECTED) {
      if (!formik.values.reason?.trim()) {
        setReasonError(t('errors.required'));
        return;
      }
    }

    setReasonError(null);
    setLoading(true);

    await entryRequestService
      .changeEntryRequestState(data.id, { state, reason: formik.values.reason })
      .then(() => {
        handleRefresh();
        onClose();
      })
      .finally(() => setLoading(false));
  };

  const getStateValue = () => {
    const translatedState = t(`invitation.entryRequest.state.${data.state}`);
    if (data.state === EntryRequestState.REJECTED)
      return translatedState + ' | ' + getFirstLastNameObj(data.rejectedBy) + ' | ' + formatDate(i18n, data.rejectedOn, true);
    if (data.state === EntryRequestState.APPROVED)
      return translatedState + ' | ' + getFirstLastNameObj(data.approvedBy) + ' | ' + formatDate(i18n, data.approvedOn, true);
    return translatedState;
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>, formik: FormikProps<FormValues>) => {
    formik.handleChange(event);
    // Clear error when user starts typing
    if (reasonError) {
      setReasonError(null);
    }
  };

  return (
    <QcsModal open={true} onClose={onClose}>
      <QcsModalBox>
        <SectionHeader title="invitation.entryRequest.detail" />
        <FormikDebounce<FormValues>
          initialValues={{
            id: data.id,
            dateFrom: data.dateFrom || '',
            dateTo: data.dateTo || '',
            timeFrom: data.timeFrom || '',
            supplierEmployees: data.supplierEmployees || [],
            workplaces: data.workplaces || [],
            establishment: data.establishment,
            state: data.state,
            invitation: data.invitation,
            reason: '',
          }}
          onSubmit={() => {}}
        >
          {(formik) => {
            const justifyContent: 'space-between' | 'flex-end' | 'flex-start' | undefined =
              formik.values.state === EntryRequestState.CREATED
                ? 'space-between'
                : formik.values.state === EntryRequestState.REJECTED
                ? 'flex-end'
                : formik.values.state === EntryRequestState.APPROVED
                ? 'flex-start'
                : undefined;
            return (
              <Form>
                <FormContainer>
                  <InvitationPersonAutocomplete
                    name="supplierEmployees"
                    label={t('invitation.entryRequest.selectedWorkers')}
                    persons={[]}
                    multiple
                    disabled
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                    }}
                  >
                    <DatePicker name="dateFrom" label={t('invitation.entryRequest.from')} disabled />
                    <DatePicker name="dateTo" label={t('invitation.entryRequest.to')} disabled />
                    <QcsTimePicker label={t('invitation.entryRequest.entryTime')} disabled name="timeFrom" />
                  </Box>

                  <EstablishmentAutocomplete establishments={establishments} name="establishment" label={t('invitation.entryRequest.establishment')} required disabled hideOnSingleOption={false} />

                  <InvitationWorkplaceAutocomplete
                    multiple
                    name="workplaces"
                    label={t('invitation.entryRequest.workplaces')}
                    required
                    establishmentIds={formik.values.establishment ? [formik.values.establishment.id] : []}
                    showEstablishmentName={true}
                    disabled
                  />

                  <QcsTextField label={t('invitation.entryRequest.table.status')} name="state" value={getStateValue()} disabled fullWidth />
                  {data.state === EntryRequestState.REJECTED && data.rejectionReason && (
                    <QcsTextField
                      name="rejectionReason"
                      value={data.rejectionReason}
                      disabled
                      fullWidth
                      label={t('invitation.entryRequest.reason')}
                    />
                  )}

                  <QcsAutocomplete<InvitationDto>
                    name="invitation"
                    loading={false}
                    loadingError={false}
                    getOptionId={(inv) => inv.id!}
                    getOptionName={(inv) => inv.name || ''}
                    label={t('invitation.entryRequest.table.invitation')}
                    disabled
                  />
                  {!disabled && hasRole(identity.roles, [UserRole.ManagerWorkplace, UserRole.ManagerOsah]) && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: justifyContent,
                        flexShrink: 0,
                        flexDirection: { xs: 'column', sm: formik.values.state === EntryRequestState.APPROVED ? 'column' : 'row' },
                        gap: { xs: 2, sm: 0 },
                        alignItems: { xs: 'stretch', sm: formik.values.state === EntryRequestState.CREATED ? 'flex-end' : justifyContent },
                      }}
                    >
                      {formik.values.state !== EntryRequestState.REJECTED && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: { xs: 'stretch', sm: 'flex-end' },
                            alignItems: 'flex-start',
                            flexShrink: 0,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1, sm: 0 },
                          }}
                        >
                          <QcsLoadingButton
                            sx={{
                              mr: { xs: 0, sm: 2 },
                              height: '36px',
                              width: { xs: '100%', sm: 'auto' },
                            }}
                            variant="outlined"
                            loading={loading}
                            onClick={() => changeState(EntryRequestState.REJECTED, formik)}
                          >
                            {t('invitation.entryRequest.reject')}
                          </QcsLoadingButton>
                          <Field name="reason">
                            {({ field }: any) => (
                              <QcsTextField
                                {...field}
                                label={t('invitation.entryRequest.reason')}
                                onChange={(e) => handleReasonChange(e, formik)}
                                error={!!reasonError}
                                helperText={reasonError}
                                sx={{
                                  width: { xs: '100%', sm: '300px' },
                                  '& .MuiOutlinedInput-root': {
                                    height: '36px',
                                    fontSize: '14px',
                                  },
                                  '& .MuiInputLabel-root': {
                                    fontSize: '14px',
                                    transform: 'translate(14px, 9px) scale(1)',
                                    '&.MuiInputLabel-shrink': {
                                      transform: 'translate(14px, -9px) scale(0.75)',
                                    },
                                  },
                                }}
                              />
                            )}
                          </Field>
                        </Box>
                      )}
                      {formik.values.state !== EntryRequestState.APPROVED && (
                        <QcsLoadingButton
                          variant="contained"
                          sx={{
                            height: '36px',
                            order: { xs: 2, sm: 1 },
                          }}
                          loading={loading}
                          onClick={() => changeState(EntryRequestState.APPROVED, formik)}
                        >
                          {t('invitation.entryRequest.approve')}
                        </QcsLoadingButton>
                      )}
                    </Box>
                  )}
                </FormContainer>
              </Form>
            );
          }}
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
};
